import { Suspense, useEffect, useContext } from 'react';

// Motion
import { motion } from 'framer-motion';

// Context

// Auth
import { FirebaseContext } from '../context/firebaseContext';
import { BackendContext } from '../context/backendContext';

// Router
import { Navigate, Outlet, Routes as RRDRoutes, Route, useLocation } from 'react-router-dom';

// Routes
import { Routes } from './routes';

// Layouts
import VerticalLayout from '../layout/VerticalLayout';
import HorizontalLayout from '../layout/HorizontalLayout';
import FullLayout from '../layout/FullLayout';

// Components
import Home from '../view/home';
import Login from '../view/login';
import PasswordResetPage, { PASSWORD_RESET_PAGE_PATH } from '../view/password-reset/PasswordResetPage';
import ConversationResume from '../view/pages/conversations/resume.jsx';
import { useCustomise } from '../context/customiseContext';

import Payment from '../view/pages/payment/index.jsx';
import { Spin } from 'antd';

const FullScreenSpin = (
  <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    <Spin size="large" />
  </div>
);

export default function Router() {
  // Context
  const { theme, setTheme, direction } = useCustomise();

  const { user, isAuthInitialized } = useContext(FirebaseContext);
  const { refreshVersion } = useContext(BackendContext);

  const location = useLocation();

  useEffect(() => {
    // Dark Mode
    document.querySelector('body').classList.add(localStorage.getItem('theme') || theme);
    setTheme(localStorage.getItem('theme') || theme);

    // RTL
    if (direction === 'ltr') {
      document.querySelector('html').setAttribute('dir', 'ltr');
    } else if (direction === 'rtl') {
      document.querySelector('html').setAttribute('dir', 'rtl');
    }
  }, []);

  // Refresh version when the user change his path location -> will window.location.reload if the version is'nt up to date
  useEffect(() => {
    refreshVersion(true);
  }, [location, refreshVersion]);

  // All of the available layouts
  const Layouts = { VerticalLayout, FullLayout, HorizontalLayout };

  // Return Filtered Array of Routes & Paths
  const LayoutRoutesAndPaths = (layout) => {
    const LayoutRoutes = [];
    const LayoutPaths = [];
    if (Routes) {
      // Checks if Route layout or Default layout matches current layout
      Routes.filter(
        (route) =>
          route.layout === layout && (LayoutRoutes.push(route), LayoutPaths.push(route.path))
      );
    }

    return { LayoutRoutes, LayoutPaths };
  };

  // Return Route to Render
  const ResolveRoutes = () => {
    return Object.keys(Layouts).map((layout, index) => {
      const { LayoutRoutes, LayoutPaths } = LayoutRoutesAndPaths(layout);

      let LayoutTag = Layouts[layout];

      return LayoutPaths.map((path) => (
        <Route
          key={`${index}-${path}`}
          element={
            <LayoutTag>
              <Outlet />
            </LayoutTag>
          }
        >
          {LayoutRoutes.map((route) => {
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <Suspense fallback={FullScreenSpin}>
                    {route.layout === 'FullLayout' ? (
                      <route.component />
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          type: 'spring',
                          duration: 0.5,
                          delay: 0.5,
                        }}
                      >
                        <route.component />
                      </motion.div>
                    )}
                  </Suspense>
                }
              />
            );
          })}
        </Route>
      ));
    });
  };

  if (user) {
    return (
      <>
        <Payment />
        <RRDRoutes>
          {ResolveRoutes()}
          <Route
            path="/"
            element={
              <Layouts.VerticalLayout>
                <Home />
              </Layouts.VerticalLayout>
            }
          />
          <Route path="/contact/:idcontact/conversation/summary" element={<ConversationResume />} />
          <Route path={PASSWORD_RESET_PAGE_PATH} element={<PasswordResetPage />} />
          <Route path="*" element={<Navigate to={`/`} replace />} />
        </RRDRoutes>
      </>
    );
  } else if (!isAuthInitialized) {   
    // Prevent public routes to show before the auth was even init
    return FullScreenSpin;
  } else {
    const fullPath = `${location.pathname}${location.search}${location.hash}`;
    return (
      <RRDRoutes>
        <Route path="/" element={<Login />} />
        <Route path="/signin" element={<Login />} />
        <Route path={PASSWORD_RESET_PAGE_PATH} element={<PasswordResetPage />} />
        <Route path="/contact/:idcontact/conversation/summary" element={<ConversationResume />} />
        <Route
          path="*"
          element={<Navigate to={`/?redirect=${encodeURIComponent(fullPath)}`} replace />}
        />
      </RRDRoutes>
    );
  }
}
